import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { createInquiry } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { useSettings } from "@/context/SettingsContext";
import { SERVICES } from "@/data/site";
import { Loader2 } from "lucide-react";

const empty = {
  name: "",
  phone: "",
  email: "",
  package: "",
  event_type: "",
  preferred_date: "",
  message: "",
};

export default function BookingDialog() {
  const { open, setOpen, presetPackage } = useBooking();
  const { settings } = useSettings();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...empty, package: presetPackage });
  }, [open, presetPackage]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await createInquiry(form);
      toast.success("Request sent! We'll reach out shortly to lock in your date.");
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 956-684-8211.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-lg bg-[#0A0A0A] border border-rgv-darkred text-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl italic font-extrabold uppercase tracking-wide">
            Book Your <span className="text-rgv-red">Gaming Party</span>
          </DialogTitle>
          <DialogDescription className="text-rgv-gray">
            $150 non-refundable deposit holds your date. We&rsquo;ll confirm details by phone.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bk-name" className="text-rgv-gray">Name *</Label>
              <Input
                id="bk-name"
                data-testid="booking-name-input"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                className="bg-black border-rgv-maroon text-white mt-1 h-12"
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="bk-phone" className="text-rgv-gray">Phone *</Label>
              <Input
                id="bk-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                data-testid="booking-phone-input"
                value={form.phone}
                onChange={(e) => set("phone")(e.target.value)}
                className="bg-black border-rgv-maroon text-white mt-1 h-12"
                placeholder="956-000-0000"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bk-email" className="text-rgv-gray">Email</Label>
            <Input
              id="bk-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              data-testid="booking-email-input"
              value={form.email}
              onChange={(e) => set("email")(e.target.value)}
              className="bg-black border-rgv-maroon text-white mt-1 h-12"
              placeholder="you@email.com"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-rgv-gray">Package</Label>
              <Select value={form.package} onValueChange={set("package")}>
                <SelectTrigger
                  data-testid="booking-package-select"
                  className="bg-black border-rgv-maroon text-white mt-1 h-12"
                >
                  <SelectValue placeholder="Choose a package" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-rgv-maroon text-white">
                  {settings.packages.map((p) => (
                    <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-rgv-gray">Event Type</Label>
              <Select value={form.event_type} onValueChange={set("event_type")}>
                <SelectTrigger
                  data-testid="booking-event-select"
                  className="bg-black border-rgv-maroon text-white mt-1 h-12"
                >
                  <SelectValue placeholder="What's the occasion?" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-rgv-maroon text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="bk-date" className="text-rgv-gray">Preferred Date</Label>
            <Input
              id="bk-date"
              type="date"
              data-testid="booking-date-input"
              value={form.preferred_date}
              onChange={(e) => set("preferred_date")(e.target.value)}
              className="bg-black border-rgv-maroon text-white mt-1 h-12"
            />
          </div>

          <div>
            <Label htmlFor="bk-msg" className="text-rgv-gray">Message</Label>
            <Textarea
              id="bk-msg"
              data-testid="booking-message-input"
              value={form.message}
              onChange={(e) => set("message")(e.target.value)}
              className="bg-black border-rgv-maroon text-white mt-1"
              placeholder="Tell us about your event, headcount, location..."
              rows={3}
            />
          </div>

          <button
            type="submit"
            data-testid="booking-submit-btn"
            disabled={loading}
            className="btn-skew btn-red w-full py-3 text-lg disabled:opacity-60"
          >
            <span className="flex items-center gap-2">
              {loading && <Loader2 className="h-5 w-5 animate-spin" />}
              {loading ? "Sending..." : "Send Request"}
            </span>
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
